import type { GridRow, GridRowOrder } from '@hcc/types';
import { GRID_ORDER_CATEGORIES } from '@hcc/types';
import type { ConditionSourceField } from './ConditionEditor';

interface GridRowOrderEditorProps {
  row: GridRow;
  /** Yes/No fields on the form, offered as the optional "only order if" gate. */
  gateFields: ConditionSourceField[];
  onToggle: (on: boolean) => void;
  onPatch: (patch: Partial<GridRowOrder>) => void;
}

const cellClass = 'rounded-token-sm border border-line-strong/20 bg-surface px-2 py-1.5 text-sm';

/**
 * The per-row procurement block: marks a grid row as an orderable item
 * (feeding the Kit to order screen) with its canonical name, category and
 * optional yes/no gate.
 */
export function GridRowOrderEditor({ row, gateFields, onToggle, onPatch }: GridRowOrderEditorProps) {
  return (
    <div className="mt-2 border-t border-line/10 pt-2">
      <label className="flex items-center gap-2 text-xs font-medium text-fg-muted">
        <input type="checkbox" checked={!!row.order} onChange={(e) => onToggle(e.target.checked)} />
        Orderable item (counts towards Kit to order)
      </label>
      {row.order ? (
        <div className="mt-2 grid grid-cols-2 gap-2">
          <label className="col-span-2 block text-xs text-fg-muted">
            Item name
            <input
              value={row.order.item}
              onChange={(e) => onPatch({ item: e.target.value })}
              className={'mt-1 block w-full ' + cellClass}
              placeholder="e.g. Polo Shirt - Black"
            />
          </label>
          <label className="col-span-2 block text-xs text-fg-muted">
            Category
            <select
              value={row.order.category}
              onChange={(e) => onPatch({ category: e.target.value as GridRowOrder['category'] })}
              className={'mt-1 block w-full ' + cellClass}
            >
              {GRID_ORDER_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <label className="col-span-2 block text-xs text-fg-muted">
            Only order if…
            <select
              value={row.order.gate_field_key ?? ''}
              onChange={(e) =>
                onPatch(
                  e.target.value
                    ? {
                        gate_field_key: e.target.value,
                        gate_skip_value: row.order!.gate_skip_value ?? 'yes',
                      }
                    : { gate_field_key: undefined, gate_skip_value: undefined },
                )
              }
              className={'mt-1 block w-full ' + cellClass}
            >
              <option value="">Always order</option>
              {gateFields.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </select>
          </label>
          {row.order.gate_field_key ? (
            <label className="col-span-2 block text-xs text-fg-muted">
              Don't order when the answer is
              <select
                value={row.order.gate_skip_value ?? 'yes'}
                onChange={(e) => onPatch({ gate_skip_value: e.target.value })}
                className={'mt-1 block w-full ' + cellClass}
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </label>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
